<template>
  <div class="transactions-container">
    <!-- Header -->
    <div class="page-header">
      <h1>Transactions Portefeuilles</h1>
      <router-link
        to="/wallets"
        class="btn btn-secondary"
      >
        <i class="fas fa-arrow-left" /> Retour aux Portefeuilles
      </router-link>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filter-group">
        <label>Statut</label>
        <select
          v-model="filters.status"
          @change="loadTransactions"
        >
          <option value="">
            Tous
          </option>
          <option value="pending">
            En Attente
          </option>
          <option value="confirmed">
            Confirmé
          </option>
          <option value="paid">
            Payé
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label>Date début</label>
        <input
          v-model="filters.start_date"
          type="date"
          @change="loadTransactions"
        >
      </div>

      <div class="filter-group">
        <label>Date fin</label>
        <input
          v-model="filters.end_date"
          type="date"
          @change="loadTransactions"
        >
      </div>

      <div class="filter-group search-group">
        <label>Rechercher</label>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Référence, fournisseur..."
          @input="debounceSearch"
        >
      </div>

      <div class="filter-group">
        <label>&nbsp;</label>
        <button
          class="btn btn-secondary"
          @click="resetFilters"
        >
          <i class="fas fa-redo" /> Réinitialiser
        </button>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="table-container">
      <table
        v-if="transactions.length > 0"
        class="transactions-table"
      >
        <thead>
          <tr>
            <th>Référence</th>
            <th>Fournisseur</th>
            <th>Type</th>
            <th>Statut</th>
            <th class="text-right">
              Montant
            </th>
            <th>Commandes</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="tx in transactions"
            :key="tx.id"
          >
            <td>
              <strong>{{ tx.reference }}</strong>
            </td>
            <td>{{ tx.supplier_name }}</td>
            <td>{{ tx.type_display }}</td>
            <td>
              <span
                class="status-badge"
                :class="tx.status"
              >
                {{ tx.status_display }}
              </span>
            </td>
            <td class="text-right amount">
              {{ formatAmount(tx.amount) }} DT
            </td>
            <td>
              <span class="badge badge-info">{{ tx.orders_count }}</span>
            </td>
            <td>{{ formatDate(tx.created_at) }}</td>
            <td class="actions-cell">
              <router-link
                :to="`/wallets/transactions/${tx.id}`"
                class="btn-action"
                title="Voir"
              >
                <i class="fas fa-eye" />
              </router-link>
              <button
                v-if="tx.status === 'confirmed'"
                class="btn-action btn-pay"
                title="Marquer comme payé"
                @click="markAsPaid(tx)"
              >
                <i class="fas fa-money-bill-wave" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-else-if="!loading"
        class="empty-state"
      >
        <i class="fas fa-exchange-alt" />
        <p>Aucune transaction trouvée</p>
      </div>

      <div
        v-if="loading"
        class="loading-state"
      >
        <i class="fas fa-spinner fa-spin" />
        <p>Chargement...</p>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="pagination.total > pagination.pageSize"
      class="pagination-container"
    >
      <button
        :disabled="pagination.page === 1"
        class="btn btn-sm"
        @click="changePage(pagination.page - 1)"
      >
        <i class="fas fa-chevron-left" />
      </button>
      <span class="page-info">
        Page {{ pagination.page }} / {{ Math.ceil(pagination.total / pagination.pageSize) }}
      </span>
      <button
        :disabled="pagination.page >= Math.ceil(pagination.total / pagination.pageSize)"
        class="btn btn-sm"
        @click="changePage(pagination.page + 1)"
      >
        <i class="fas fa-chevron-right" />
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'WalletTransactions',
  data() {
    return {
      transactions: [],
      loading: false,
      filters: {
        status: '',
        start_date: '',
        end_date: '',
        search: '',
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
      },
      searchTimeout: null,
    }
  },
  created() {
    // Check for supplier filter in query params
    if (this.$route.query.supplier) {
      this.filters.supplier = this.$route.query.supplier
    }
    this.loadTransactions()
  },
  methods: {
    async loadTransactions() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.pageSize,
        }

        if (this.filters.status) params.status = this.filters.status
        if (this.filters.start_date) params.start_date = this.filters.start_date
        if (this.filters.end_date) params.end_date = this.filters.end_date
        if (this.filters.search) params.search = this.filters.search
        if (this.filters.supplier) params.supplier = this.filters.supplier

        const response = await axios.get('/api/wallets/wallet-transactions/', { params })
        this.transactions = response.data.results || response.data
        this.pagination.total = response.data.count || this.transactions.length
      } catch (error) {
        this.$toast.error('Erreur lors du chargement des transactions')
      } finally {
        this.loading = false
      }
    },

    async markAsPaid(transaction) {
      const confirmed = await this.$bvModal.msgBoxConfirm('Marquer cette transaction comme payée ?', {
        title: 'Confirmation',
        okVariant: 'primary',
        okTitle: 'Confirmer',
        cancelTitle: 'Annuler',
      })
      if (!confirmed) return

      try {
        await axios.post(`/api/wallets/wallet-transactions/${transaction.id}/mark-paid/`)
        this.$toast.success('Transaction marquée comme payée')
        this.loadTransactions()
      } catch (error) {
        this.$toast.error('Erreur lors du marquage')
      }
    },

    debounceSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1
        this.loadTransactions()
      }, 300)
    },

    resetFilters() {
      this.filters = {
        status: '',
        start_date: '',
        end_date: '',
        search: '',
      }
      this.pagination.page = 1
      this.loadTransactions()
    },

    changePage(page) {
      this.pagination.page = page
      this.loadTransactions()
    },

    formatAmount(amount) {
      if (!amount) return '0.000'
      return parseFloat(amount).toFixed(3)
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.transactions-container {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }
}

.filters-section {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    &.search-group {
      flex: 1;
      min-width: 200px;
    }

    label {
      font-size: 0.75rem;
      font-weight: 500;
      color: #64748b;
    }

    input, select {
      padding: 0.5rem 0.75rem;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      font-size: 0.875rem;

      &:focus {
        outline: none;
        border-color: #6366f1;
      }
    }
  }
}

.table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.transactions-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    background: #f8fafc;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    color: #64748b;
  }

  .text-right {
    text-align: right;
  }

  .amount {
    font-family: 'Monaco', 'Courier New', monospace;
    font-weight: 600;
    color: #1e293b;
  }

  .badge {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;

    &.badge-info {
      background: #e0e7ff;
      color: #6366f1;
    }
  }

  .actions-cell {
    display: flex;
    gap: 0.5rem;

    .btn-action {
      padding: 0.5rem;
      border-radius: 6px;
      color: #64748b;
      background: none;
      border: none;
      cursor: pointer;

      &:hover {
        background: #f1f5f9;
        color: #6366f1;
      }

      &.btn-pay {
        color: #10b981;
        &:hover {
          background: #d1fae5;
          color: #059669;
        }
      }
    }
  }
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;

  &.pending {
    background: #fef3c7;
    color: #f59e0b;
  }

  &.confirmed {
    background: #d1fae5;
    color: #10b981;
  }

  &.paid {
    background: #ede9fe;
    color: #8b5cf6;
  }

  &.cancelled {
    background: #fee2e2;
    color: #ef4444;
  }
}

.empty-state, .loading-state {
  text-align: center;
  padding: 3rem;
  color: #64748b;

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;

  .page-info {
    font-size: 0.875rem;
    color: #64748b;
  }
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  border: none;

  &.btn-secondary {
    background: #f1f5f9;
    color: #64748b;
    &:hover { background: #e2e8f0; }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
